import { useId, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * A hook that provides a live region for announcing dynamic content changes to assistive technologies.
 * The live region is rendered in a portal to document.body to avoid affecting parent layout.
 * @param {React.HTMLAttributes<HTMLSpanElement>} [props={}] - Additional props to pass to the live region element.
 * @returns {{
 *  announce: (message: string) => void,
 *  liveRegion: React.ReactElement
 * }}
 */
export function useLiveRegion(props = {}) {
  const [announcement, setAnnouncement] = useState('');
  const announcerId = useId();

  const announce = message => {
    requestAnimationFrame(() => setAnnouncement(message));
  };

  const liveRegionElement = (
    <span id={announcerId} role="status" className="sr-only" {...props}>
      {announcement}
    </span>
  );

  // Render in a portal to avoid affecting parent layout
  const liveRegion =
    typeof document !== 'undefined'
      ? createPortal(liveRegionElement, document.body)
      : liveRegionElement;

  return { announce, liveRegion };
}
